/**
 * Normalize file name by removing accents and replacing special characters
 * @param fileName - The original file name
 * @returns Normalized file name safe for storage and URLs
 */
export const normalizeFileName = (fileName: string): string => {
  // Split filename and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';

  // Normalize Unicode characters (NFD = decomposed form, separates base characters from diacritics)
  let normalized = name.normalize('NFD');

  // Remove diacritical marks (accents)
  normalized = normalized.replace(/[\u0300-\u036f]/g, '');

  // Replace special characters with safe alternatives
  normalized = normalized
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ýÿ]/g, 'y')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/[ß]/g, 'ss')
    .replace(/[æ]/g, 'ae')
    .replace(/[œ]/g, 'oe')
    // Replace other special characters with underscore or remove
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    // Remove multiple consecutive underscores
    .replace(/_{2,}/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');

  // Ensure the name is not empty
  if (!normalized) {
    normalized = 'file';
  }

  return normalized + extension;
};

/**
 * Create a new File object with a normalized name
 * @param file - The original File object
 * @returns A new File object with normalized name
 */
export const normalizeFile = (file: File): File => {
  const normalizedName = normalizeFileName(file.name);

  // If the name is already normalized, return the original file
  if (normalizedName === file.name) {
    return file;
  }

  // Create a new File object with the normalized name
  return new File([file], normalizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
};
